import React, { useEffect, useState } from 'react';
import { IRespostaCircularizacao, StatusValidacaoResposta } from '../interfaces/ICircularizacao';
import { CircularizacaoService } from '../services/CircularizacaoService';
import { justificativaPadraoClassificacao } from '../utils/justificativasPadrao';

interface Props {
  esteiraId: number;
  onFechar: () => void;
}

const OPCOES_CLASSIFICACAO: { value: StatusValidacaoResposta; label: string }[] = [
  { value: 'CONCORDANTE', label: 'Concordante' },
  { value: 'DIVERGENTE', label: 'Divergente' },
  { value: 'PARCIAL', label: 'Parcial' },
  { value: 'INCONCLUSIVA', label: 'Inconclusiva' },
  { value: 'RECUSA', label: 'Recusa' },
];

// Cards 11/12 do Épico Backend — respostas candidatas (recém-chegadas pelo webhook) e em análise,
// à espera de classificação (RN-010: classificar nunca conclui o procedimento sozinho — concluir é
// uma ação separada, feita em CartaDetalheModal depois que a carta reflete a classificação).
const RespostasPendentesModal: React.FC<Props> = ({ esteiraId, onFechar }) => {
  const [respostas, setRespostas] = useState<IRespostaCircularizacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [classificandoId, setClassificandoId] = useState<string | undefined>();
  const [statusSelecionado, setStatusSelecionado] = useState<StatusValidacaoResposta>('CONCORDANTE');
  const [justificativa, setJustificativa] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState('');

  const carregar = async () => {
    setCarregando(true);
    const lista = await CircularizacaoService.listarRespostasPendentes(esteiraId);
    setRespostas(lista);
    setCarregando(false);
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esteiraId]);

  const abrirClassificacao = (resposta: IRespostaCircularizacao) => {
    setClassificandoId(resposta.id);
    setStatusSelecionado('CONCORDANTE');
    setJustificativa(justificativaPadraoClassificacao('CONCORDANTE'));
  };

  // Só atualiza a justificativa sugerida se o auditor ainda não mexeu nela — troca de status não
  // deve apagar um texto que a pessoa já começou a escrever.
  const alterarStatusSelecionado = (novoStatus: StatusValidacaoResposta) => {
    setJustificativa((atual) =>
      atual === justificativaPadraoClassificacao(statusSelecionado) ? justificativaPadraoClassificacao(novoStatus) : atual
    );
    setStatusSelecionado(novoStatus);
  };

  const confirmarClassificacao = async () => {
    if (!classificandoId || !justificativa.trim()) return;
    setSalvando(true);
    const resultado = await CircularizacaoService.classificarResposta(classificandoId, statusSelecionado, justificativa.trim());
    setSalvando(false);
    setMensagem(resultado.mensagem);
    if (resultado.sucesso) {
      setClassificandoId(undefined);
      setJustificativa('');
      await carregar();
    }
  };

  return (
    <div className='circ-modal-backdrop' onMouseDown={(e) => e.target === e.currentTarget && onFechar()}>
      <div className='circ-modal circ-modal-lg'>
        <div className='circ-modal-header'>
          <h4>Respostas Recebidas — Pendentes de Classificação</h4>
          <button type='button' className='btn btn-sm btn-icon btn-light' aria-label='Fechar' onClick={onFechar}>
            <i className='bi bi-x-lg' />
          </button>
        </div>
        <div className='circ-modal-body'>
          {carregando && <div className='text-muted text-center py-3'>Carregando...</div>}

          {!carregando && respostas.length === 0 && (
            <div className='circ-empty-state'>
              <i className='bi bi-envelope-check circ-empty-state-icon' />
              <div className='circ-empty-state-title'>Nenhuma resposta pendente de classificação</div>
            </div>
          )}

          {!carregando && respostas.length > 0 && (
            <div className='d-flex flex-column gap-2'>
              {respostas.map((r) => (
                <div key={r.id} className='circ-contato-item' style={{ display: 'block' }}>
                  <div className='d-flex justify-content-between align-items-start'>
                    <div>
                      <strong style={{ fontSize: 13 }}>{r.remetenteEmail}</strong>
                      <div className='text-muted' style={{ fontSize: 12 }}>{r.assunto}</div>
                      <div className='text-muted' style={{ fontSize: 11 }}>
                        Recebida em {new Date(r.recebidoEm).toLocaleString('pt-BR')}
                      </div>
                    </div>
                    {classificandoId !== r.id && (
                      <button type='button' className='btn btn-sm btn-primary' onClick={() => abrirClassificacao(r)}>
                        Classificar
                      </button>
                    )}
                  </div>

                  {classificandoId === r.id && (
                    <div className='mt-2'>
                      <div className='circ-form-group'>
                        <label htmlFor={`resp-status-${r.id}`}>Classificação</label>
                        <select
                          id={`resp-status-${r.id}`}
                          className='circ-form-control'
                          value={statusSelecionado}
                          onChange={(e) => alterarStatusSelecionado(e.target.value as StatusValidacaoResposta)}
                        >
                          {OPCOES_CLASSIFICACAO.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className='circ-form-group'>
                        <label className='circ-campo-obrigatorio'>Justificativa</label>
                        <textarea
                          className='circ-form-control'
                          rows={2}
                          value={justificativa}
                          onChange={(e) => setJustificativa(e.target.value)}
                          placeholder='Ex.: Saldo confirmado sem divergências em relação ao registrado.'
                        />
                      </div>
                      <div className='d-flex gap-2'>
                        <button type='button' className='btn btn-sm btn-light' onClick={() => setClassificandoId(undefined)}>
                          Cancelar
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-primary'
                          disabled={salvando || !justificativa.trim()}
                          onClick={confirmarClassificacao}
                        >
                          {salvando && <span className='spinner-border spinner-border-sm me-1' />}
                          Confirmar Classificação
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {mensagem && <div className='mt-3' style={{ fontSize: 12.5, color: '#47586b' }}>{mensagem}</div>}
        </div>
        <div className='circ-modal-footer'>
          <button type='button' className='btn btn-sm btn-light' onClick={onFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default RespostasPendentesModal;
